import React, { useState, useEffect } from 'react';
import {
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { hmrcAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const HMRC = () => {
  const [submissions, setSubmissions] = useState([]);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // view, submit-fps, submit-eps, generate-p45, generate-p60
  const [submissionData, setSubmissionData] = useState({
    tax_year: '2024-25',
    tax_period: new Date().getMonth() + 1,
    employee_ids: []
  });

  const { user } = useAuth();

  const fetchSubmissions = async () => {
    try {
      const response = await hmrcAPI.getSubmissions();
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Error fetching HMRC submissions:', error);
    }
  };

  const fetchComplianceStatus = async () => {
    try {
      const response = await hmrcAPI.getComplianceStatus();
      setComplianceStatus(response.data);
    } catch (error) {
      console.error('Error fetching compliance status:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSubmissions(),
          fetchComplianceStatus()
        ]);
      } catch (error) {
        setError('Failed to load HMRC data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSubmitFPS = async (e) => {
    e.preventDefault();
    try {
      await hmrcAPI.submitFPS(submissionData);
      setShowModal(false);
      fetchSubmissions();
      fetchComplianceStatus();
      alert('FPS submitted successfully');
    } catch (error) {
      console.error('Error submitting FPS:', error);
      alert(error.response?.data?.message || 'Failed to submit FPS');
    }
  };

  const handleSubmitEPS = async (e) => {
    e.preventDefault();
    try {
      await hmrcAPI.submitEPS({
        tax_year: submissionData.tax_year,
        tax_period: submissionData.tax_period
      });
      setShowModal(false);
      fetchSubmissions();
      fetchComplianceStatus();
      alert('EPS submitted successfully');
    } catch (error) {
      console.error('Error submitting EPS:', error);
      alert(error.response?.data?.message || 'Failed to submit EPS');
    }
  };

  const handleGenerateP45 = async (employeeId, leavingData) => {
    try {
      await hmrcAPI.generateP45(employeeId, leavingData);
      alert('P45 generated successfully');
    } catch (error) {
      console.error('Error generating P45:', error);
      alert('Failed to generate P45');
    }
  };

  const handleBulkP60Generation = async () => {
    try {
      await hmrcAPI.generateBulkP60s({
        tax_year: '2024-25'
      });
      alert('P60s generated successfully');
    } catch (error) {
      console.error('Error generating P60s:', error);
      alert('Failed to generate P60s');
    }
  };

  const openModal = (mode, submission = null) => {
    setModalMode(mode);
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
    setSubmissionData({
      tax_year: '2024-25',
      tax_period: new Date().getMonth() + 1,
      employee_ids: []
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted':
        return <PaperAirplaneIcon className="w-5 h-5 text-blue-500" />;
      case 'accepted':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">HMRC Compliance</h1>
          <p className="text-sm text-gray-600 mt-1">Manage HMRC submissions and compliance</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => openModal('submit-fps')}
            className="btn-primary"
          >
            <PaperAirplaneIcon className="w-4 h-4 mr-2" />
            Submit FPS
          </button>
          <button
            onClick={() => openModal('submit-eps')}
            className="btn-secondary"
          >
            <DocumentCheckIcon className="w-4 h-4 mr-2" />
            Submit EPS
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Compliance Status */}
      {complianceStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShieldCheckIcon className={`h-8 w-8 ${
                  complianceStatus.overall_compliance === 'compliant' 
                    ? 'text-green-600' 
                    : 'text-yellow-600'
                }`} />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Compliance Status
                  </dt>
                  <dd className={`text-lg font-medium capitalize ${
                    complianceStatus.overall_compliance === 'compliant' 
                      ? 'text-green-600' 
                      : 'text-yellow-600'
                  }`}>
                    {complianceStatus.overall_compliance || 'Unknown'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    FPS Submissions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {complianceStatus.fps_submissions_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    EPS Submissions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {complianceStatus.eps_submissions_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className={`h-8 w-8 ${
                  (complianceStatus.overdue_submissions || 0) > 0 
                    ? 'text-red-600' 
                    : 'text-gray-400'
                }`} />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Overdue Submissions
                  </dt>
                  <dd className={`text-lg font-medium ${
                    (complianceStatus.overdue_submissions || 0) > 0 
                      ? 'text-red-600' 
                      : 'text-gray-900'
                  }`}>
                    {complianceStatus.overdue_submissions || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleBulkP60Generation}
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            <DocumentArrowDownIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <div className="text-sm font-medium text-gray-900">Generate P60s</div>
            <div className="text-xs text-gray-500">Bulk generate end of year P60 forms</div>
          </button>

          <button
            onClick={() => openModal('generate-p45')}
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            <DocumentCheckIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <div className="text-sm font-medium text-gray-900">Generate P45</div>
            <div className="text-xs text-gray-500">Generate P45 for leaving employee</div>
          </button>

          <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
            <ShieldCheckIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <div className="text-sm font-medium text-gray-900">Compliance Check</div>
            <div className="text-xs text-gray-500">Run automated compliance validation</div>
          </div>
        </div>
      </div>

      {/* Recent Submissions */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Recent Submissions</h2>
        </div>

        {submissions.length === 0 ? (
          <div className="text-center py-8">
            <DocumentCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
            <p className="text-gray-600">Submit your first FPS or EPS to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tax Year/Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HMRC Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(submission.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.submission_type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.tax_year} / Period {submission.tax_period}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.employee_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(submission.status)}`}>
                        {submission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.hmrc_reference || 'Pending'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.submitted_at 
                        ? new Date(submission.submitted_at).toLocaleDateString('en-GB')
                        : 'Not submitted'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openModal('view', submission)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {modalMode === 'submit-fps' ? 'Submit FPS (Full Payment Submission)' :
                   modalMode === 'submit-eps' ? 'Submit EPS (Employer Payment Summary)' :
                   modalMode === 'generate-p45' ? 'Generate P45' :
                   modalMode === 'generate-p60' ? 'Generate P60' :
                   'Submission Details'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {modalMode === 'submit-fps' ? (
                <form onSubmit={handleSubmitFPS} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Year
                      </label>
                      <select
                        className="form-input"
                        value={submissionData.tax_year}
                        onChange={(e) => setSubmissionData({
                          ...submissionData,
                          tax_year: e.target.value
                        })}
                      >
                        <option value="2024-25">2024-25</option>
                        <option value="2023-24">2023-24</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Period (Month)
                      </label>
                      <select
                        className="form-input"
                        value={submissionData.tax_period}
                        onChange={(e) => setSubmissionData({
                          ...submissionData,
                          tax_period: parseInt(e.target.value)
                        })}
                      >
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2024, i, 1).toLocaleString('en-GB', { month: 'long' })} ({i + 1})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-blue-800">
                      This will submit payroll data for all active employees for the selected tax period.
                      Make sure all payroll has been processed and verified before submission.
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Submit FPS
                    </button>
                  </div>
                </form>
              ) : modalMode === 'submit-eps' ? (
                <form onSubmit={handleSubmitEPS} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Year
                      </label>
                      <select
                        className="form-input"
                        value={submissionData.tax_year}
                        onChange={(e) => setSubmissionData({
                          ...submissionData,
                          tax_year: e.target.value
                        })}
                      >
                        <option value="2024-25">2024-25</option>
                        <option value="2023-24">2023-24</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Period (Month)
                      </label>
                      <select
                        className="form-input"
                        value={submissionData.tax_period}
                        onChange={(e) => setSubmissionData({
                          ...submissionData,
                          tax_period: parseInt(e.target.value)
                        })}
                      >
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2024, i, 1).toLocaleString('en-GB', { month: 'long' })} ({i + 1})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-sm text-yellow-800">
                      Submit EPS if no employees were paid during this period or to report employer-only liabilities.
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Submit EPS
                    </button>
                  </div>
                </form>
              ) : selectedSubmission && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Submission Type</label>
                      <div className="mt-1 text-sm text-gray-900">{selectedSubmission.submission_type}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedSubmission.status)}`}>
                          {selectedSubmission.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax Year</label>
                      <div className="mt-1 text-sm text-gray-900">{selectedSubmission.tax_year}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax Period</label>
                      <div className="mt-1 text-sm text-gray-900">{selectedSubmission.tax_period}</div>
                    </div>
                  </div>

                  {selectedSubmission.hmrc_reference && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">HMRC Reference</label>
                      <div className="mt-1 text-sm text-gray-900">{selectedSubmission.hmrc_reference}</div>
                    </div>
                  )}

                  {selectedSubmission.error_details && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Error Details</label>
                      <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                        {selectedSubmission.error_details}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {selectedSubmission.submitted_at 
                        ? new Date(selectedSubmission.submitted_at).toLocaleString('en-GB')
                        : 'Not submitted'
                      }
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HMRC;